import type { Tier, BillingPeriod } from "@mw/shared/pricing";
import { getCreativeDollarPrice, getCreativeIncludedUnits, getTiers, MUSIC_FINETUNE_USD } from "@mw/shared/pricing";
import { getPrices } from "./utils";

// Re-export shared value types and constructors
export type { FormattedValue, CurrencyUnit } from "./pricing-value-types";
export {
  numberValue,
  currencyValue,
  hoursValue,
  minutesValue,
  minutesApproxValue,
  charactersValue,
  textValue,
  booleanValue,
  linkValue,
} from "./pricing-value-types";
import type { FormattedValue } from "./pricing-value-types";
import {
  numberValue,
  currencyValue,
  hoursValue,
  minutesApproxValue,
  booleanValue,
  linkValue,
  textValue,
} from "./pricing-value-types";

export type { BillingPeriod } from "@mw/shared/pricing";

import type { Section } from "./pricing-sections";

// ---------------------------------------------------------------------------
// Translation key types (matching PlatformPricing namespace in en.json)
// ---------------------------------------------------------------------------

export type PlatformPricingRowTranslationKey =
  // Text-to-speech
  | "tts_minutes_included"
  | "tts_extra_minutes"
  | "tts_audio_quality"
  // Speech-to-text
  | "stt_hours_included"
  | "stt_price_per_hour"
  | "stt_additional_hours"
  | "stt_entity_detection"
  | "stt_keyterm_prompting"
  // Speech-to-speech
  | "s2s_minutes_included"
  | "s2s_additional_minutes"
  // Sound effects
  | "sfx_included_generations"
  | "sfx_extra_generation_cost"
  // Voice isolator
  | "vi_minutes_included"
  | "vi_additional_minutes"
  // Music
  | "music_included_minutes"
  | "music_additional_minute_cost"
  | "music_restrictions"
  | "music_finetune_cost"
  // Image & Video
  | "iv_images_included"
  | "iv_videos_included"
  | "iv_upscaling"
  | "iv_lipsync"
  // Conversational AI (V1 platform tab only)
  | "cai_minutes_included"
  | "cai_extra_minute_cost"
  | "cai_concurrency_limit"
  | "cai_text_message_cost"
  // Dubbing (automatic & studio share the same row keys)
  | "dub_minutes_included"
  | "dub_extra_minute_cost"
  // Voices
  | "voices_custom"
  | "voices_synthetic"
  | "voices_instant_cloning"
  | "voices_professional_clones"
  // Studio
  | "studio_projects_limit"
  // Audio Native
  | "an_access"
  | "an_embed_limit";

export type PlatformPricingSubSectionTranslationKey =
  | "multilingual_v2_v3"
  | "flash"
  | "text_to_speech_ui"
  | "scribe_v1_v2_api"
  | "scribe_v1_v2_ui"
  | "scribe_v2_realtime_api"
  | "with_watermark"
  | "no_watermark";

// ---------------------------------------------------------------------------
// Row & section types (same contract as api-pricing-data / agents-pricing-data)
// ---------------------------------------------------------------------------

/** Value returned by a row's getValue, with an optional subtitle */
export interface PlatformCellValue {
  value: FormattedValue;
  /** Optional secondary line rendered below the main value (e.g. "~$0.09/min") */
  subtitle?: FormattedValue;
}

export interface PlatformPricingRow {
  labelKey: PlatformPricingRowTranslationKey;
  getValue: (tier: Tier, period: BillingPeriod) => PlatformCellValue;
  tooltipKey?: string;
  size?: "small" | "medium" | "large";
}

export interface PlatformPricingSubSection {
  labelKey?: PlatformPricingSubSectionTranslationKey;
  rows: PlatformPricingRow[];
}

export interface PlatformPricingSectionConfig {
  subSections: PlatformPricingSubSection[];
}

/** Wraps a FormattedValue into a PlatformCellValue, with optional subtitle */
const cell = (value: FormattedValue, subtitle?: FormattedValue): PlatformCellValue => ({ value, subtitle });

/**
 * Returns the overage dollar price per credit-unit for a tier.
 * Always uses "monthly" pricing so overage rates stay constant regardless of
 * the billing-period toggle (the annual discount only affects the subscription,
 * not per-unit overage charges).
 * For the free tier (no subscription price), returns 2× the Creator tier's rate.
 */
const getCreativeOverageDollarPrice = (
  creditRatePerUnit: number,
  tier: Tier,
  _period: BillingPeriod,
): number | undefined => {
  if (tier.planId === "free") {
    const creatorTier = getTiers()["creator"];
    const creatorPrice = getCreativeDollarPrice(creditRatePerUnit, creatorTier, "monthly");
    return creatorPrice !== undefined ? creatorPrice * 2 : undefined;
  }
  return getCreativeDollarPrice(creditRatePerUnit, tier, "monthly");
};

// ---------------------------------------------------------------------------
// Text-to-Speech rows
// ---------------------------------------------------------------------------

/**
 * 192 kbps high-quality audio is only available from Pro and above —
 * Creator drops back to the base quality.
 */
const TTS_AUDIO_QUALITY_ROW: PlatformPricingRow = {
  labelKey: "tts_audio_quality",
  tooltipKey: "quality_and_formats.audio_quality.tooltip",
  getValue: tier => {
    if (tier.planId === "creator") {
      return cell({ type: "text", textKey: "audio_quality_starter" });
    }
    return cell({ type: "text", textKey: `audio_quality_${tier.planId}` });
  },
};

const TTS_MULTILINGUAL_ROWS: PlatformPricingRow[] = [
  {
    labelKey: "tts_minutes_included",
    tooltipKey: "text_to_speech.highest_quality_minutes.tooltip",
    getValue: tier => {
      const creative = tier.platformConfig.creative;
      const minutes =
        creative.textToSpeech.overrideIncludedMultilingualMinutes ??
        getCreativeIncludedUnits(creative.textToSpeech.nonTurboCreditsPerThousandChars, tier);
      return cell(minutesApproxValue(minutes, "none"));
    },
  },
  {
    labelKey: "tts_extra_minutes",
    tooltipKey: "text_to_speech.highest_quality_price.tooltip",
    getValue: (tier, period) => {
      const creative = tier.platformConfig.creative;
      const price = getCreativeOverageDollarPrice(creative.textToSpeech.nonTurboCreditsPerThousandChars, tier, period);
      return cell(currencyValue(price, "flat_approx"));
    },
  },
  TTS_AUDIO_QUALITY_ROW,
];

// ---------------------------------------------------------------------------
// Speech-to-Text rows
// ---------------------------------------------------------------------------

/** Scribe UI rows. Values are derived from credit rates. */
const STT_SCRIBE_UI_ROWS: PlatformPricingRow[] = [
  {
    labelKey: "stt_hours_included",
    tooltipKey: "speech_to_text.speech_to_text_hours.tooltip",
    getValue: tier => {
      const creative = tier.platformConfig.creative;
      const hours =
        creative.speechToText.overrideIncludedHours ??
        (() => {
          const minutes = getCreativeIncludedUnits(creative.speechToText.creditsPerMinute, tier);
          return minutes !== undefined ? minutes / 60 : undefined;
        })();
      return cell(hoursValue(hours));
    },
  },
  {
    labelKey: "stt_additional_hours",
    getValue: (tier, period) => {
      const creative = tier.platformConfig.creative;
      const pricePerMinute = getCreativeOverageDollarPrice(creative.speechToText.creditsPerMinute, tier, period);
      const pricePerHour = pricePerMinute !== undefined ? pricePerMinute * 60 : undefined;
      return cell(currencyValue(pricePerHour));
    },
  },
];

// ---------------------------------------------------------------------------
// Speech-to-Speech rows
// ---------------------------------------------------------------------------

const S2S_ROWS: PlatformPricingRow[] = [
  {
    labelKey: "s2s_minutes_included",
    tooltipKey: "speech_to_speech.speech_to_speech_minutes_included.tooltip",
    getValue: tier => {
      const creative = tier.platformConfig.creative;
      const minutes =
        creative.voiceChanger.overrideIncludedMinutes ??
        getCreativeIncludedUnits(creative.voiceChanger.creditsPerMinute, tier);
      return cell(numberValue(minutes));
    },
  },
  {
    labelKey: "s2s_additional_minutes",
    tooltipKey: "speech_to_speech.speech_to_speech_additional_minutes.tooltip",
    getValue: (tier, period) => {
      const creative = tier.platformConfig.creative;
      const price = getCreativeOverageDollarPrice(creative.voiceChanger.creditsPerMinute, tier, period);
      return cell(currencyValue(price, "flat_approx"));
    },
  },
];

// ---------------------------------------------------------------------------
// Sound Effects rows
// ---------------------------------------------------------------------------

const SFX_ROWS: PlatformPricingRow[] = [
  {
    labelKey: "sfx_included_generations",
    tooltipKey: "sound_effects.number_of_sound_effects.tooltip",
    getValue: tier => {
      const creative = tier.platformConfig.creative;
      const generations =
        creative.soundEffects.overrideIncludedGenerations ??
        getCreativeIncludedUnits(creative.soundEffects.creditsPerGeneration, tier);
      return cell(numberValue(generations));
    },
  },
  {
    labelKey: "sfx_extra_generation_cost",
    tooltipKey: "sound_effects.extra_sound_effects.tooltip",
    getValue: (tier, period) => {
      const creative = tier.platformConfig.creative;
      const price = getCreativeOverageDollarPrice(creative.soundEffects.creditsPerGeneration, tier, period);
      if (!price) return cell(booleanValue(false));
      return cell(currencyValue(price));
    },
  },
];

// ---------------------------------------------------------------------------
// Voice Isolator rows
// ---------------------------------------------------------------------------

const VI_ROWS: PlatformPricingRow[] = [
  {
    labelKey: "vi_minutes_included",
    tooltipKey: "voice_isolator.minutes_included.tooltip",
    getValue: tier => {
      const creative = tier.platformConfig.creative;
      const minutes =
        creative.voiceIsolator.overrideIncludedMinutes ??
        getCreativeIncludedUnits(creative.voiceIsolator.creditsPerMinute, tier);
      return cell(numberValue(minutes));
    },
  },
  {
    labelKey: "vi_additional_minutes",
    tooltipKey: "voice_isolator.additional_minutes.tooltip",
    getValue: (tier, period) => {
      const creative = tier.platformConfig.creative;
      const price = getCreativeOverageDollarPrice(creative.voiceIsolator.creditsPerMinute, tier, period);
      return cell(currencyValue(price, "flat_approx"));
    },
  },
];

// ---------------------------------------------------------------------------
// Music rows
// ---------------------------------------------------------------------------

const MUSIC_ROWS: PlatformPricingRow[] = [
  {
    labelKey: "music_included_minutes",
    tooltipKey: "eleven_music.included_minutes.tooltip",
    getValue: tier => {
      const creative = tier.platformConfig.creative;
      const rawMinutes =
        creative.music.overrideIncludedMinutes ?? getCreativeIncludedUnits(creative.music.creditsPerMinute, tier);
      return cell(numberValue(rawMinutes !== undefined ? Math.round(rawMinutes) : undefined));
    },
  },
  {
    labelKey: "music_additional_minute_cost",
    tooltipKey: "eleven_music.additional_minutes_cost_usd.tooltip",
    getValue: (tier, period) => {
      const creative = tier.platformConfig.creative;
      const price = getCreativeOverageDollarPrice(creative.music.creditsPerMinute, tier, period);
      if (!price) return cell(booleanValue(false));
      return cell(currencyValue(price));
    },
  },
  {
    labelKey: "music_restrictions",
    tooltipKey: "eleven_music.restrictions.tooltip",
    size: "large",
    getValue: tier => {
      const commercialUse = tier.products.music.features.commercialUse;
      if (!commercialUse) return cell(booleanValue(false));
      const textKey =
        commercialUse === "excluding_enterprise_and_streaming"
          ? "excluding_enterprise_and_streaming"
          : "excluding_enterprise";
      return cell(linkValue(textKey, "/music-terms", "_blank"));
    },
  },
  {
    labelKey: "music_finetune_cost",
    getValue: () => cell(currencyValue(MUSIC_FINETUNE_USD, "flat")),
  },
];

// ---------------------------------------------------------------------------
// Image & Video rows
// ---------------------------------------------------------------------------

const IV_ROWS: PlatformPricingRow[] = [
  {
    labelKey: "iv_images_included",
    tooltipKey: "image_video.images_included.tooltip",
    getValue: tier => {
      const images = tier.products.imageVideo.limits.images;
      if (!images) return cell(booleanValue(false));
      return cell(textValue("up_to_images", { count: images }));
    },
  },
  {
    labelKey: "iv_videos_included",
    tooltipKey: "image_video.videos_included.tooltip",
    getValue: tier => {
      const seconds = tier.products.imageVideo.limits.videoSeconds;
      if (!seconds) return cell(booleanValue(false));
      return cell(textValue("up_to_seconds", { count: seconds }));
    },
  },
  {
    labelKey: "iv_upscaling",
    tooltipKey: "image_video.upscaling.tooltip",
    getValue: tier => {
      const images = tier.products.imageVideo.limits.upscalingImages ?? 0;
      const seconds = tier.products.imageVideo.limits.upscalingVideoSeconds ?? 0;
      if (images === 0 && seconds === 0) return cell(booleanValue(false));
      return cell(textValue("images_and_seconds_of_video", { images, seconds }));
    },
  },
  {
    labelKey: "iv_lipsync",
    tooltipKey: "image_video.lipsync.tooltip",
    getValue: tier => {
      const seconds = tier.products.imageVideo.limits.lipsyncSeconds;
      if (!seconds) return cell(booleanValue(false));
      return cell(textValue("count_seconds", { count: seconds }));
    },
  },
];

// ---------------------------------------------------------------------------
// Conversational AI rows (V1 platform tab only; V2 hides the tab via feature flag)
// ---------------------------------------------------------------------------

const CAI_HARDCODED_PRICE_PER_MINUTE: Record<string, { monthly: number; annual: number }> = {
  pro: { monthly: 0.09, annual: 0.08 },
  scale: { monthly: 0.09, annual: 0.08 },
  scale_2024_08_10: { monthly: 0.09, annual: 0.08 },
};

const CAI_ROWS: PlatformPricingRow[] = [
  {
    labelKey: "cai_minutes_included",
    tooltipKey: "conversational_ai.conversational_ai_minutes_included.tooltip",
    getValue: (tier, period) => {
      const value = numberValue(tier.products.conversationalAI.limits.includedMinutes);
      const hardcoded = CAI_HARDCODED_PRICE_PER_MINUTE[tier.planId];
      if (hardcoded) {
        const pricePerMinute = period === "yearly" ? hardcoded.annual : hardcoded.monthly;
        return cell(value, currencyValue(pricePerMinute, "flat_approx", 3));
      }
      const { price, discountedPrice } = getPrices(tier.pricing, period);
      const subscriptionPrice = period === "yearly" ? (discountedPrice ?? price) : price;
      const minutesIncluded = tier.products.conversationalAI.limits.includedMinutes;
      if (subscriptionPrice && minutesIncluded && minutesIncluded > 0) {
        return cell(value, currencyValue(subscriptionPrice / minutesIncluded, "flat_approx", 3));
      }
      return cell(value);
    },
  },
  {
    labelKey: "cai_extra_minute_cost",
    tooltipKey: "conversational_ai.conversational_ai_extra_minutes.tooltip",
    getValue: (tier, period) => {
      const pricing = tier.products.conversationalAI.pricing;
      const cost =
        period === "yearly" && pricing.extraMinuteCostAnnual !== undefined
          ? pricing.extraMinuteCostAnnual
          : pricing.extraMinuteCost;
      if (cost === undefined) return cell(booleanValue(false));
      const unit = tier.planId === "scale_2024_08_10" ? ("flat" as const) : ("flat_approx" as const);
      return cell(currencyValue(cost, unit, 3));
    },
  },
  {
    labelKey: "cai_concurrency_limit",
    tooltipKey: "conversational_ai.concurrency_limit.tooltip",
    getValue: tier => cell(numberValue(tier.products.conversationalAI.limits.concurrencyLimit)),
  },
  {
    labelKey: "cai_text_message_cost",
    tooltipKey: "conversational_ai.conversational_ai_text_message_cost.tooltip",
    getValue: tier => {
      const cost = tier.products.conversationalAI.pricing.textMessageCost;
      if (cost === undefined) return cell(booleanValue(false));
      return cell(currencyValue(cost, "flat", 3));
    },
  },
];

// ---------------------------------------------------------------------------
// Dubbing rows (automatic dubbing & dubbing studio, each with watermark variants)
// ---------------------------------------------------------------------------

const makeAutomaticDubbingRows = (watermark: "watermark" | "no_watermark"): PlatformPricingRow[] => [
  {
    labelKey: "dub_minutes_included",
    tooltipKey:
      watermark === "watermark"
        ? "automatic_dubbing.automatic_dubbing_watermark_minutes.tooltip"
        : "automatic_dubbing.automatic_dubbing_no_watermark_minutes.tooltip",
    getValue: tier => {
      const creative = tier.platformConfig.creative;
      const overrideMinutes =
        watermark === "watermark"
          ? creative.dubbing.overrideIncludedAutoWatermarkMinutes
          : creative.dubbing.overrideIncludedAutoNoWatermarkMinutes;
      const creditsPerMinute =
        watermark === "watermark"
          ? creative.dubbing.autoWatermarkCreditsPerMinute
          : creative.dubbing.autoNoWatermarkCreditsPerMinute;
      if (creditsPerMinute === undefined) {
        if (tier.planId === "free") return cell(numberValue(0));
        return cell(booleanValue(false));
      }
      const minutes = overrideMinutes ?? getCreativeIncludedUnits(creditsPerMinute, tier);
      return cell(numberValue(minutes !== undefined ? Math.round(minutes) : undefined));
    },
  },
  {
    labelKey: "dub_extra_minute_cost",
    tooltipKey: "dubbing.extra_minutes.tooltip",
    getValue: (tier, period) => {
      const creative = tier.platformConfig.creative;
      let creditsPerMinute: number | undefined =
        watermark === "watermark"
          ? creative.dubbing.autoWatermarkCreditsPerMinute
          : creative.dubbing.autoNoWatermarkCreditsPerMinute;
      if (creditsPerMinute === undefined && tier.planId === "free") {
        const creatorCreative = getTiers()["creator"].platformConfig.creative;
        creditsPerMinute =
          watermark === "watermark"
            ? creatorCreative.dubbing.autoWatermarkCreditsPerMinute
            : creatorCreative.dubbing.autoNoWatermarkCreditsPerMinute;
      }
      if (creditsPerMinute === undefined) return cell(booleanValue(false));
      const price = getCreativeOverageDollarPrice(creditsPerMinute, tier, period);
      return cell(currencyValue(price, "flat"));
    },
  },
];

const makeDubbingStudioRows = (watermark: "watermark" | "no_watermark"): PlatformPricingRow[] => [
  {
    labelKey: "dub_minutes_included",
    tooltipKey:
      watermark === "watermark"
        ? "dubbing_studio.dubbing_studio_watermark_minutes.tooltip"
        : "dubbing_studio.dubbing_studio_no_watermark_minutes.tooltip",
    getValue: tier => {
      const creative = tier.platformConfig.creative;
      const overrideMinutes =
        watermark === "watermark"
          ? creative.dubbing.overrideIncludedStudioWatermarkMinutes
          : creative.dubbing.overrideIncludedStudioNoWatermarkMinutes;
      const creditsPerMinute =
        watermark === "watermark"
          ? creative.dubbing.studioWatermarkCreditsPerMinute
          : creative.dubbing.studioNoWatermarkCreditsPerMinute;
      if (creditsPerMinute === undefined) {
        if (tier.planId === "free") return cell(numberValue(0));
        return cell(booleanValue(false));
      }
      const minutes = overrideMinutes ?? getCreativeIncludedUnits(creditsPerMinute, tier);
      return cell(numberValue(minutes !== undefined ? Math.round(minutes) : undefined));
    },
  },
  {
    labelKey: "dub_extra_minute_cost",
    tooltipKey: "dubbing.extra_minutes.tooltip",
    getValue: (tier, period) => {
      const creative = tier.platformConfig.creative;
      let creditsPerMinute: number | undefined =
        watermark === "watermark"
          ? creative.dubbing.studioWatermarkCreditsPerMinute
          : creative.dubbing.studioNoWatermarkCreditsPerMinute;
      if (creditsPerMinute === undefined && tier.planId === "free") {
        const creatorCreative = getTiers()["creator"].platformConfig.creative;
        creditsPerMinute =
          watermark === "watermark"
            ? creatorCreative.dubbing.studioWatermarkCreditsPerMinute
            : creatorCreative.dubbing.studioNoWatermarkCreditsPerMinute;
      }
      if (creditsPerMinute === undefined) return cell(booleanValue(false));
      const price = getCreativeOverageDollarPrice(creditsPerMinute, tier, period);
      return cell(currencyValue(price, "flat"));
    },
  },
];

// ---------------------------------------------------------------------------
// Voices rows
// ---------------------------------------------------------------------------

const VOICES_ROWS: PlatformPricingRow[] = [
  {
    labelKey: "voices_custom",
    tooltipKey: "voices.custom_voices.tooltip",
    getValue: tier => {
      const count = tier.products.voices.limits.customVoices;
      if (count === undefined) return cell({ type: "unavailable" });
      return cell(textValue("count_voices", { count }));
    },
  },
  {
    labelKey: "voices_synthetic",
    tooltipKey: "voices.synthetic_voice_design.tooltip",
    getValue: () => cell(booleanValue(true)),
  },
  {
    labelKey: "voices_instant_cloning",
    tooltipKey: "voices.instant_voice_clones.tooltip",
    getValue: tier => cell(booleanValue(tier.products.voices.features.instantVoiceCloning)),
  },
  {
    labelKey: "voices_professional_clones",
    tooltipKey: "voices.professional_voice_clones.tooltip",
    getValue: tier => {
      const slots = tier.products.voices.limits.pvcSlots;
      if (slots === undefined || slots === 0) return cell(booleanValue(false));
      const textKey = slots === 1 ? "count_pvc" : "count_pvcs";
      return cell(textValue(textKey, { count: slots }));
    },
  },
];

// ---------------------------------------------------------------------------
// Studio rows
// ---------------------------------------------------------------------------

const STUDIO_ROWS: PlatformPricingRow[] = [
  {
    labelKey: "studio_projects_limit",
    tooltipKey: "studio.projects_limit.tooltip",
    getValue: tier => cell(numberValue(tier.products.projects.limits.maxProjects)),
  },
];

// ---------------------------------------------------------------------------
// Audio Native rows
// ---------------------------------------------------------------------------

const AUDIO_NATIVE_ROWS: PlatformPricingRow[] = [
  {
    labelKey: "an_access",
    tooltipKey: "audio_native.audio_native_access.tooltip",
    getValue: tier => cell(booleanValue(tier.products.audioNative.enabled)),
  },
  {
    labelKey: "an_embed_limit",
    tooltipKey: "audio_native.audio_native_limit.tooltip",
    getValue: tier => cell(numberValue(tier.products.audioNative.limits.maxEmbeds)),
  },
];

// ---------------------------------------------------------------------------
// Section configurations
// ---------------------------------------------------------------------------

/**
 * Sections supported by the platform pricing data layer.
 * - `productions` and `dubbing` are excluded (handled separately)
 * - `conversational_ai` is present in V1; the V2 feature flag hides it at the tab level
 */
export type PlatformPricingSection = Exclude<Section, "productions" | "dubbing">;

const PLATFORM_PRICING_SECTIONS: Record<PlatformPricingSection, PlatformPricingSectionConfig> = {
  text_to_speech: {
    subSections: [{ labelKey: "text_to_speech_ui", rows: TTS_MULTILINGUAL_ROWS }],
  },
  speech_to_text: {
    subSections: [{ labelKey: "scribe_v1_v2_ui", rows: STT_SCRIBE_UI_ROWS }],
  },
  speech_to_speech: {
    subSections: [{ rows: S2S_ROWS }],
  },
  sound_effects: {
    subSections: [{ rows: SFX_ROWS }],
  },
  voice_isolator: {
    subSections: [{ rows: VI_ROWS }],
  },
  eleven_music: {
    subSections: [{ rows: MUSIC_ROWS }],
  },
  image_video: {
    subSections: [{ rows: IV_ROWS }],
  },
  conversational_ai: {
    subSections: [{ rows: CAI_ROWS }],
  },
  automatic_dubbing: {
    subSections: [
      { labelKey: "with_watermark", rows: makeAutomaticDubbingRows("watermark") },
      { labelKey: "no_watermark", rows: makeAutomaticDubbingRows("no_watermark") },
    ],
  },
  dubbing_studio: {
    subSections: [
      { labelKey: "with_watermark", rows: makeDubbingStudioRows("watermark") },
      { labelKey: "no_watermark", rows: makeDubbingStudioRows("no_watermark") },
    ],
  },
  voices: {
    subSections: [{ rows: VOICES_ROWS }],
  },
  studio: {
    subSections: [{ rows: STUDIO_ROWS }],
  },
  audio_native: {
    subSections: [{ rows: AUDIO_NATIVE_ROWS }],
  },
};

export const getPlatformPricingSections = (): Record<PlatformPricingSection, PlatformPricingSectionConfig> =>
  PLATFORM_PRICING_SECTIONS;

// All translation keys (used by server component to build translation maps)
export const ALL_ROW_KEYS: PlatformPricingRowTranslationKey[] = [
  "tts_minutes_included",
  "tts_extra_minutes",
  "tts_audio_quality",
  "stt_hours_included",
  "stt_price_per_hour",
  "stt_additional_hours",
  "stt_entity_detection",
  "stt_keyterm_prompting",
  "s2s_minutes_included",
  "s2s_additional_minutes",
  "sfx_included_generations",
  "sfx_extra_generation_cost",
  "vi_minutes_included",
  "vi_additional_minutes",
  "music_included_minutes",
  "music_additional_minute_cost",
  "music_restrictions",
  "music_finetune_cost",
  "iv_images_included",
  "iv_videos_included",
  "iv_upscaling",
  "iv_lipsync",
  "cai_minutes_included",
  "cai_extra_minute_cost",
  "cai_concurrency_limit",
  "cai_text_message_cost",
  "dub_minutes_included",
  "dub_extra_minute_cost",
  "voices_custom",
  "voices_synthetic",
  "voices_instant_cloning",
  "voices_professional_clones",
  "studio_projects_limit",
  "an_access",
  "an_embed_limit",
];

export const ALL_SUBSECTION_KEYS: PlatformPricingSubSectionTranslationKey[] = [
  "multilingual_v2_v3",
  "flash",
  "text_to_speech_ui",
  "scribe_v1_v2_api",
  "scribe_v1_v2_ui",
  "scribe_v2_realtime_api",
  "with_watermark",
  "no_watermark",
];

/**
 * All tooltip keys used across sections.
 * These reference paths in the ClientKeys.Pricing namespace and are resolved
 * server-side via `t.raw()` so the HTML tooltip content stays out of the client bundle.
 */
export const ALL_TOOLTIP_KEYS: string[] = [
  // Text-to-speech
  "text_to_speech.highest_quality_minutes.tooltip",
  "text_to_speech.turbo_minutes.tooltip",
  "text_to_speech.turbo_minutes_v2.tooltip",
  "text_to_speech.highest_quality_price.tooltip",
  "text_to_speech.turbo_price.tooltip",
  "text_to_speech.turbo_price_v2.tooltip",
  "quality_and_formats.audio_quality.tooltip",
  // Speech-to-text
  "speech_to_text.speech_to_text_hours.tooltip",
  "speech_to_text.entity_detection.tooltip",
  "speech_to_text.keyterm_prompting.tooltip",
  // Speech-to-speech
  "speech_to_speech.speech_to_speech_minutes_included.tooltip",
  "speech_to_speech.speech_to_speech_additional_minutes.tooltip",
  // Sound effects
  "sound_effects.number_of_sound_effects.tooltip",
  "sound_effects.extra_sound_effects.tooltip",
  // Voice isolator
  "voice_isolator.minutes_included.tooltip",
  "voice_isolator.additional_minutes.tooltip",
  // Music
  "eleven_music.included_minutes.tooltip",
  "eleven_music.additional_minutes_cost_usd.tooltip",
  "eleven_music.restrictions.tooltip",
  // Image & Video
  "image_video.images_included.tooltip",
  "image_video.videos_included.tooltip",
  "image_video.upscaling.tooltip",
  "image_video.lipsync.tooltip",
  // Conversational AI
  "conversational_ai.conversational_ai_minutes_included.tooltip",
  "conversational_ai.conversational_ai_extra_minutes.tooltip",
  "conversational_ai.concurrency_limit.tooltip",
  "conversational_ai.conversational_ai_text_message_cost.tooltip",
  // Dubbing
  "automatic_dubbing.automatic_dubbing_watermark_minutes.tooltip",
  "automatic_dubbing.automatic_dubbing_no_watermark_minutes.tooltip",
  "dubbing_studio.dubbing_studio_watermark_minutes.tooltip",
  "dubbing_studio.dubbing_studio_no_watermark_minutes.tooltip",
  "dubbing.extra_minutes.tooltip",
  // Voices
  "voices.custom_voices.tooltip",
  "voices.synthetic_voice_design.tooltip",
  "voices.instant_voice_clones.tooltip",
  "voices.professional_voice_clones.tooltip",
  // Studio
  "studio.projects_limit.tooltip",
  // Audio Native
  "audio_native.audio_native_access.tooltip",
  "audio_native.audio_native_limit.tooltip",
];
